import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { sitesV2Api, SiteV2 } from '../../../api/sitesV2Api';
import { dashboardCountersApi } from '../../../api/dashboardCountersApi';
import { questionsV2Api, QuestionV2 } from '../../../api/questionsV2Api';
import config from '../../../config/config';
import styles from './ObjectsList.module.css';
import CreateSiteModal from './CreateSiteModal';

interface ObjectsListProps {
  onProjectSelect?: (project: SiteV2) => void;
  onQuestionSelect?: (question: QuestionV2) => void;
  type?: 'projects' | 'questions';
  siteId?: number; // Для вопросов конкретного объекта
}

interface ListItem {
  id: number;
  name: string;
  closed?: boolean;
  avatarUrl?: string; // URL аватарки объекта
  lastActivity?: {
    type: 'question' | 'session' | 'none';
    text: string;
    time: Date;
    author?: string;
  };
  unreadCount?: number;
  activeShiftsCount?: number;
  // Дополнительные поля для вопросов
  text?: string;
  userId?: number;
  user?: { 
    id: number;
    name: string; 
    closed: boolean;
  };
  typeOfWorks?: { 
    id: number;
    name: string;
    section?: string;
    closed: boolean;
  };
  createdAt?: string;
}

// Простое кэширование данных (в памяти, на время сессии)
const cache = new Map<string, { data: ListItem[], timestamp: number }>();
const CACHE_DURATION = 30000; // 30 секунд

const ObjectsList: React.FC<ObjectsListProps> = ({ 
  onProjectSelect, 
  onQuestionSelect, 
  type = 'projects', 
  siteId 
}) => {
  const [items, setItems] = useState<ListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  useEffect(() => {
    loadData();
  }, [type, siteId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Проверяем кэш для проектов
      if (type === 'projects') {
        const cacheKey = 'projects';
        const cached = cache.get(cacheKey);
        const now = Date.now();
        
        // Используем кэш, если данные свежие
        if (cached && (now - cached.timestamp) < CACHE_DURATION) {
          setItems(cached.data);
          setLoading(false);
          loadActiveShiftsCounts(cached.data.map((item) => item.id)).catch((err) => {
            console.warn('Не удалось обновить счётчики активных смен:', err);
          });
          // Загружаем в фоне для обновления кэша (stale-while-revalidate pattern)
          loadProjects().catch(err => {
            console.warn('Background refresh failed:', err);
          });
          return;
        }
        
        await loadProjects();
      } else if (type === 'questions' && siteId) {
        // Проверяем кэш для вопросов
        const cacheKey = `questions_${siteId}`;
        const cached = cache.get(cacheKey);
        const now = Date.now();
        
        if (cached && (now - cached.timestamp) < CACHE_DURATION) {
          setItems(cached.data);
          setLoading(false);
          // Загружаем в фоне для обновления
          loadQuestions().catch(err => {
            console.warn('Background refresh failed:', err);
          });
          return;
        }
        
        await loadQuestions();
      }
    } catch (err) {
      setError('Ошибка загрузки данных');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadActiveShiftsCounts = useCallback(async (siteIds: number[]) => {
    if (siteIds.length === 0) return;

    const counts = await Promise.all(
      siteIds.map(async (siteId) => {
        try {
          const counters = await dashboardCountersApi.getDashboardCounters(siteId);
          if (counters.activeSessions.hasAccess) {
            return { siteId, count: counters.activeSessions.count };
          }
        } catch (err) {
          console.warn(`Не удалось загрузить счётчик активных смен для объекта ${siteId}:`, err);
        }
        return { siteId, count: 0 };
      })
    );

    const countBySiteId = new Map(counts.map(({ siteId, count }) => [siteId, count]));

    setItems((prev) => {
      const next = prev.map((item) => ({
        ...item,
        activeShiftsCount: countBySiteId.get(item.id) ?? 0,
      }));
      cache.set('projects', { data: next, timestamp: Date.now() });
      return next;
    });
  }, []);

  const loadProjects = async () => {
    // Сначала проверяем кеш для быстрой загрузки
    let sites = sitesV2Api.getCachedMySites();
    
    // Если кеша нет, загружаем с сервера
    if (!sites) {
      sites = await sitesV2Api.getMySites();
    }
    
    // Преобразуем в список без дополнительных загрузок
    const itemsList: ListItem[] = sites.map((site: SiteV2) => ({
      id: site.id,
      name: site.name,
      closed: site.closed,
      avatarUrl: site.avatarUrl,
      activeShiftsCount: 0,
    }));

    // Сортируем объекты: сначала активные, потом закрытые
    itemsList.sort((a: ListItem, b: ListItem) => {
      if (a.closed === b.closed) {
        return a.name.localeCompare(b.name);
      }
      return a.closed ? 1 : -1;
    });

    setItems(itemsList);

    // Сохраняем в кэш
    cache.set('projects', { data: itemsList, timestamp: Date.now() });

    loadActiveShiftsCounts(itemsList.map((item) => item.id)).catch((err) => {
      console.warn('Не удалось обновить счётчики активных смен:', err);
    });
  };

  const loadQuestions = async () => {
    if (!siteId) return;
    
    const questions = await questionsV2Api.getSiteQuestionsAll(siteId);
    
    const questionsList: ListItem[] = questions.map((question: QuestionV2) => ({
      id: question.id,
      name: question.text,
      closed: question.closed,
      text: question.text,
      userId: question.userId,
      user: question.user,
      typeOfWorks: question.typeOfWorks,
      createdAt: question.createdAt,
      lastActivity: {
        type: 'question',
        text: question.text,
        time: new Date(question.createdAt || ''),
        author: question.user?.name || 'Неизвестно'
      }
    }));

    // Сортируем вопросы по дате создания
    questionsList.sort((a, b) => {
      if (!a.createdAt && !b.createdAt) return 0;
      if (!a.createdAt) return 1;
      if (!b.createdAt) return -1;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });

    setItems(questionsList);
    
    // Сохраняем в кэш для вопросов конкретного объекта
    if (siteId) {
      cache.set(`questions_${siteId}`, { data: questionsList, timestamp: Date.now() });
    }
  };

  const handleItemClick = (item: ListItem) => {
    setSelectedItemId(item.id);
    
    if (type === 'projects') {
      // Преобразуем ListItem обратно в SiteV2 для совместимости
      const site: SiteV2 = {
        id: item.id,
        name: item.name,
        closed: item.closed || false,
        avatarUrl: item.avatarUrl
      };
      onProjectSelect?.(site);
    } else if (type === 'questions') {
      // Преобразуем ListItem обратно в QuestionV2 для совместимости
      const question: QuestionV2 = {
        id: item.id,
        userId: item.userId || 0,
        siteId: siteId || 0,
        typeId: 1, // По умолчанию Question
        text: item.text || item.name,
        closed: item.closed || false,
        visibilityType: 0,
        createdAt: item.createdAt,
        user: item.user,
        typeOfWorks: item.typeOfWorks
      };
      onQuestionSelect?.(question);
    }
  };

  const getItemIcon = (item: ListItem) => {
    if (type === 'questions') {
      return item.closed ? 'ri-lock-line' : 'ri-question-line';
    }
    
    if (item.closed) {
      return 'ri-archive-line';
    }
    return 'ri-building-2-line';
  };

  // Мемоизируем функции форматирования
  const formatTime = useCallback((date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) {
      return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
    } else if (days === 1) {
      return 'Вчера';
    } else if (days < 7) {
      return date.toLocaleDateString('ru-RU', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
    }
  }, []);

  const truncateText = useCallback((text: string, maxLength: number = 50) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  }, []);

  // Группируем элементы: активные сверху, архив снизу
  const { activeItems, archivedItems } = useMemo(() => {
    const active = items.filter(item => !item.closed);
    const archived = items.filter(item => item.closed);
    return { activeItems: active, archivedItems: archived };
  }, [items]);

  // Мемоизируем форматированные значения для оптимизации рендеринга
  const formatItemData = useCallback((item: ListItem) => {
    return {
      formattedTime: item.lastActivity ? formatTime(item.lastActivity.time) : '',
      truncatedText: item.lastActivity && item.lastActivity.type !== 'none' 
        ? truncateText(item.lastActivity.text) 
        : ''
    };
  }, [formatTime, truncateText]);

  // Обновляем группировку
  const { activeItemsFormatted, archivedItemsFormatted } = useMemo(() => {
    const active = activeItems;
    const archived = archivedItems;
    return { activeItemsFormatted: active, archivedItemsFormatted: archived };
  }, [activeItems, archivedItems]);

  if (loading) {
    // Skeleton loader - анимированные плейсхолдеры
    return (
      <div className={styles.chatList}>
        {[...Array(6)].map((_, index) => (
          <div key={index} className={styles.skeletonItem}>
            <div className={styles.skeletonAvatar}></div>
            <div className={styles.skeletonContent}>
              <div className={styles.skeletonTitle}></div>
              <div className={styles.skeletonText}></div>
              <div className={styles.skeletonMeta}></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.chatList}>
        <div className={styles.error}>
          <i className="ri-error-warning-line"></i>
          <span>{error}</span>
          <button onClick={loadData} className={styles.retryBtn}>
            <i className="ri-refresh-line"></i>
          </button>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className={styles.chatList}>
        <div className={styles.empty}>
          <i className={type === 'projects' ? 'ri-building-line' : 'ri-question-line'}></i>
          <span>Нет доступных {type === 'projects' ? 'объектов' : 'дискуссий'}</span>
          <p>{type === 'projects' ? 'Создайте новый объект или попросите добавить вас в существующий' : 'Создайте первую дискуссию для этого объекта'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.chatList}>
      {/* Список активных элементов */}
      {activeItemsFormatted.length > 0 && (
        <div className={styles.chatSection}>
          <div className={styles.chats}>
            {activeItemsFormatted.map((item, index) => {
              const formatted = formatItemData(item);
              // Используем префикс для гарантии уникальности ключа
              const uniqueKey = `active-${item.id}-${index}`;
              return (
                <div
                  key={uniqueKey}
                  className={`${styles.chatItem} ${selectedItemId === item.id ? styles.selected : ''}`}
                  onClick={() => handleItemClick(item)}
                >
                  <div 
                    className={`${styles.chatAvatar} ${item.avatarUrl ? styles.chatAvatarWithImage : ''}`}
                  >
                    {item.avatarUrl ? (
                      <>
                        <img 
                          src={item.avatarUrl.startsWith('http') 
                            ? item.avatarUrl 
                            : `${config.api.baseURL.replace('/api/v1', '').replace('/api/v2', '')}${item.avatarUrl}`}
                          alt={item.name}
                          className={styles.chatAvatarImage}
                          loading="eager"
                          decoding="async"
                          onError={(e) => {
                            // Если загрузка изображения не удалась, скрываем изображение и показываем иконку
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const parent = target.parentElement;
                            if (parent) {
                              parent.classList.remove(styles.chatAvatarWithImage);
                            }
                            const fallback = target.nextElementSibling as HTMLElement;
                            if (fallback) {
                              fallback.style.display = 'flex';
                            }
                          }}
                        />
                        <i 
                          className={getItemIcon(item)}
                          style={{ display: 'none', visibility: 'hidden', opacity: 0 }}
                        ></i>
                      </>
                    ) : (
                      <i className={getItemIcon(item)}></i>
                    )}
                  </div>
                  
                  <div className={styles.chatContent}>
                    <div className={styles.chatHeader}>
                      <h3 className={styles.chatName}>{item.name}</h3>
                      <div className={styles.chatHeaderBadges}>
                        {type === 'projects' && (item.activeShiftsCount ?? 0) > 0 && (
                          <span
                            className={styles.activeShiftsBadge}
                            title={`${item.activeShiftsCount} активных смен`}
                          >
                            {item.activeShiftsCount}
                          </span>
                        )}
                        {type === 'questions' && item.lastActivity && item.lastActivity.type !== 'none' && (
                          <span className={styles.chatTime}>
                            {formatted.formattedTime}
                          </span>
                        )}
                      </div>
                    </div>

                    {type === 'questions' && (!!formatted.truncatedText || (item.unreadCount ?? 0) > 0) && (
                      <div className={styles.chatPreview}>
                        {formatted.truncatedText && (
                          <p className={styles.lastMessage}>
                            {formatted.truncatedText}
                          </p>
                        )}
                        {(item.unreadCount ?? 0) > 0 && (
                          <div className={styles.unreadBadge}>
                            {item.unreadCount! > 99 ? '99+' : item.unreadCount}
                          </div>
                        )}
                      </div>
                    )}

                    {type === 'questions' && item.lastActivity?.author && item.lastActivity.author.trim() !== '' && (
                      <div className={styles.chatMeta}>
                        <span className={styles.lastAuthor}>
                          {item.lastActivity.author}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Список архивных элементов */}
      {archivedItemsFormatted.length > 0 && (
        <div className={styles.chatSection}>
          <div className={styles.chats}>
            {archivedItemsFormatted.map((item, index) => {
              const formatted = formatItemData(item);
              // Используем префикс для гарантии уникальности ключа
              const uniqueKey = `archived-${item.id}-${index}`;
              return (
                <div
                  key={uniqueKey}
                  className={`${styles.chatItem} ${styles.archived} ${styles.chatItemWithAvatar} ${selectedItemId === item.id ? styles.selected : ''}`}
                  onClick={() => handleItemClick(item)}
                >
                  <div 
                    className={`${styles.chatAvatar} ${item.avatarUrl ? styles.chatAvatarWithImage : ''}`}
                  >
                    {item.avatarUrl ? (
                      <>
                        <img 
                          src={item.avatarUrl.startsWith('http') 
                            ? item.avatarUrl 
                            : `${config.api.baseURL.replace('/api/v1', '').replace('/api/v2', '')}${item.avatarUrl}`}
                          alt={item.name}
                          className={styles.chatAvatarImage}
                          loading="eager"
                          decoding="async"
                          onError={(e) => {
                            // Если загрузка изображения не удалась, скрываем изображение и показываем иконку
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const parent = target.parentElement;
                            if (parent) {
                              parent.classList.remove(styles.chatAvatarWithImage);
                            }
                            const fallback = target.nextElementSibling as HTMLElement;
                            if (fallback) {
                              fallback.style.display = 'flex';
                            }
                          }}
                        />
                        <i 
                          className={getItemIcon(item)}
                          style={{ display: 'none', visibility: 'hidden', opacity: 0 }}
                        ></i>
                      </>
                    ) : (
                      <i className={getItemIcon(item)}></i>
                    )}
                  </div>
                  
                  <div className={styles.chatContent}>
                    <div className={styles.chatHeader}>
                      <h3 className={styles.chatName}>{item.name}</h3>
                      <div className={styles.chatHeaderBadges}>
                        {type === 'projects' && (item.activeShiftsCount ?? 0) > 0 && (
                          <span
                            className={styles.activeShiftsBadge}
                            title={`${item.activeShiftsCount} активных смен`}
                          >
                            {item.activeShiftsCount}
                          </span>
                        )}
                        {type === 'questions' && item.lastActivity && item.lastActivity.type !== 'none' && (
                          <span className={styles.chatTime}>
                            {formatted.formattedTime}
                          </span>
                        )}
                      </div>
                    </div>

                    {type === 'questions' && !!formatted.truncatedText && (
                      <div className={styles.chatPreview}>
                        <p className={styles.lastMessage}>
                          {formatted.truncatedText}
                        </p>
                      </div>
                    )}

                    {type === 'questions' && item.lastActivity?.author && item.lastActivity.author.trim() !== '' && (
                      <div className={styles.chatMeta}>
                        <span className={styles.lastAuthor}>
                          {item.lastActivity.author}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* FAB кнопка для создания объекта (только для проектов) */}
      {type === 'projects' && (
        <button
          className={styles.fabButton}
          onClick={() => setIsCreateModalOpen(true)}
          aria-label="Создать новый объект"
          title="Создать новый объект"
        >
          <i className="ri-add-line"></i>
        </button>
      )}

      {/* Модальное окно создания объекта */}
      {type === 'projects' && (
        <CreateSiteModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSuccess={() => {
            // Обновляем список объектов после создания
            loadData();
          }}
        />
      )}
    </div>
  );
};export default ObjectsList;


